
import React, { useState, useEffect, useRef } from 'react';
import { Heart, Send, History, User, Sparkles, Copy, Check, X, Zap, LogOut, ArrowRight, Loader2, ArrowLeft, AlertTriangle, Search, Camera, Edit2, Upload, Link as LinkIcon, Share2 } from 'lucide-react';
import { generateLoveMessage } from './geminiService';
import { db, initDb } from './neonService';
import { Profile, PixTransaction } from './types';

const App: React.FC = () => {
  // Estados de Navegação e Dados
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<Profile | null>(null);
  const [activeTab, setActiveTab] = useState<'feed' | 'send' | 'profile'>('feed');
  const [publicProfile, setPublicProfile] = useState<Profile | null>(null);
  const [transactions, setTransactions] = useState<PixTransaction[]>([]);
  
  // Estados de UI
  const [isLoading, setIsLoading] = useState(true);
  const [dbError, setDbError] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Perfil Edição
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [avatarEditMode, setAvatarEditMode] = useState<'url' | 'upload'>('upload');
  const [newAvatarUrl, setNewAvatarUrl] = useState('');
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Formulário
  const [amount, setAmount] = useState<string>('');
  const [receiverName, setReceiverName] = useState<string>('');
  const [receiverPixKey, setReceiverPixKey] = useState<string>('');
  const [receiverAvatar, setReceiverAvatar] = useState<string>(''); 
  const [message, setMessage] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isGeneratingMessage, setIsGeneratingMessage] = useState(false);

  // Busca de usuários
  const [userSuggestions, setUserSuggestions] = useState<Profile[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const searchTimeoutRef = useRef<number | null>(null);

  // Auth Form
  const [formUsername, setFormUsername] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formPixKey, setFormPixKey] = useState('');
  const [formPassword, setFormPassword] = useState('');

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    if (path === '/' || path === '/index.html') {
      setPublicProfile(null);
      setActiveTab('feed');
    }
  };

  const fetchFeed = async () => {
    if (!db.isConfigured()) return;
    try {
      const txs = await db.getLatestTransactions();
      setTransactions(txs);
    } catch (err) {
      console.error("Erro ao carregar feed:", err);
    }
  };

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const checkRoute = async () => {
      setIsLoading(true);
      try {
        if (!db.isConfigured()) {
          setDbError(true);
        } else {
          await initDb();
        }

        const segments = currentPath.split('/').filter(p => p && p !== 'index.html');
        const username = segments[0]; 
        
        const reserved = ['feed', 'send', 'profile', 'admin', 'login', 'register'];

        if (username && !reserved.includes(username.toLowerCase())) {
          const profile = await db.getProfile(username);
          if (profile) {
            setPublicProfile(profile);
            setReceiverName(profile.display_name);
            setReceiverPixKey(profile.pix_key);
            setReceiverAvatar(profile.avatar_url);
            setActiveTab('send');
          } else {
            setPublicProfile(null);
          }
        } else {
          setPublicProfile(null);
          if (username) setActiveTab(username as any);
        }
        
        await fetchFeed();
      } catch (e) {
        console.error("Erro na rota:", e);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkRoute();
  }, [currentPath]);

  const handleReceiverNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setReceiverName(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.length >= 2) {
      setIsSearchingUsers(true);
      searchTimeoutRef.current = window.setTimeout(async () => {
        const results = await db.searchProfiles(value);
        setUserSuggestions(results);
        setIsSearchingUsers(false);
      }, 500);
    } else {
      setUserSuggestions([]);
      setIsSearchingUsers(false);
    }
  };

  const selectUser = (profile: Profile) => {
    setReceiverName(profile.display_name);
    setReceiverPixKey(profile.pix_key);
    setReceiverAvatar(profile.avatar_url);
    setUserSuggestions([]);
  };

  const handleManualAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsAuthLoading(true);
    try {
      if (authMode === 'login') {
        const profile = await db.getProfile(formUsername);
        if (profile && profile.password === formPassword) {
          setUser(profile);
          setIsLoggedIn(true);
          setShowAuthModal(false);
        } else {
          setAuthError("Usuário ou senha inválidos.");
        }
      } else {
        const newProfile = await db.registerUser({
          username: formUsername,
          display_name: formDisplayName,
          pix_key: formPixKey,
          password: formPassword,
          avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${formUsername}`,
          provider: 'credentials'
        });
        if (newProfile) {
          setUser(newProfile);
          setIsLoggedIn(true);
          setShowAuthModal(false);
        } else {
          setAuthError("Nome de usuário já em uso.");
        }
      }
    } catch (err) {
      setAuthError("Erro ao conectar ao banco de dados.");
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleGenerateMagicMessage = async () => {
    if (!receiverName || !amount) {
      alert("Precisamos do nome e do valor para gerar a mensagem! ✨");
      return;
    }
    setIsGeneratingMessage(true);
    const msg = await generateLoveMessage(user?.display_name || "Alguém", receiverName, parseFloat(amount), "carinhoso");
    setMessage(msg);
    setIsGeneratingMessage(false);
  };

  const handleSendPix = async () => {
    if (!isLoggedIn) {
      setAuthMode('login');
      setShowAuthModal(true);
      return;
    }

    const val = parseFloat(amount);
    if (!receiverName || isNaN(val) || val <= 0 || !message || !receiverPixKey) {
      alert("Por favor, preencha todos os campos corretamente! ❤️");
      return;
    }
    
    setIsSending(true);
    const txData = {
      sender_id: user?.id || 'visitor',
      sender_name: user?.display_name || 'Visitante',
      sender_avatar_url: user?.avatar_url || 'https://api.dicebear.com/7.x/avataaars/svg?seed=visitor',
      receiver_id: receiverPixKey.toString(),
      receiver_name: receiverName,
      receiver_avatar_url: receiverAvatar || `https://api.dicebear.com/7.x/initials/svg?seed=${receiverName}`,
      amount: val,
      message: message,
      has_reward: false,
      is_public: true
    };

    const success = await db.createTransaction(txData);

    if (success) {
      alert("Mimo registrado! ✨ Agora basta realizar o Pix real para o destinatário.");
      setAmount('');
      setMessage('');
      if (!publicProfile) {
        setReceiverName('');
        setReceiverPixKey('');
      }
      await fetchFeed();
      setActiveTab('feed');
      navigate('/');
    } else {
      alert("Não foi possível registrar no mural. Tente novamente mais tarde.");
    }
    setIsSending(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("A imagem deve ter no máximo 2MB!");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setNewAvatarUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveAvatar = async () => {
    if (!user || !newAvatarUrl) return;
    setIsSavingAvatar(true);
    const success = await db.updateAvatar(user.id, newAvatarUrl);
    if (success) {
      setUser({ ...user, avatar_url: newAvatarUrl });
      setIsEditingAvatar(false);
      setNewAvatarUrl('');
      alert("Foto de perfil atualizada!");
    } else {
      alert("Erro ao atualizar foto.");
    }
    setIsSavingAvatar(false);
  };

  const handleCopyLink = () => {
    if (!user) return;
    const url = `${window.location.origin}/${user.username}`;
    navigator.clipboard.writeText(url);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleShareSocial = async () => {
    if (!user) return;
    const url = `${window.location.origin}/${user.username}`;
    const text = `Ei! Vem ver meu perfil no Pix Love ✨ É uma plataforma linda pra trocar mimos. Entra pelo meu link, cria sua conta e me manda um Pix Love grátis pra aparecer no meu mural! 💖: ${url}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Pix Love ✨',
          text: text,
          url: url,
        });
      } catch (err) {
        console.error("Erro ao compartilhar:", err);
      }
    } else {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const goHome = () => {
    navigate('/');
  };

  const handleOpenSendTab = () => {
    if (!isLoggedIn) {
      setAuthMode('login');
      setShowAuthModal(true);
    } else {
      setActiveTab('send');
      navigate('/send');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fff5f7]">
        <div className="text-center animate-pulse">
          <Heart size={64} className="text-pink-500 mx-auto mb-4" fill="currentColor" />
          <p className="text-pink-600 font-black text-xl tracking-tight">Preparando o Amor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-0 md:pt-20">
      {dbError && (
        <div className="fixed top-0 left-0 right-0 z-[200] bg-red-600 text-white p-2 text-center text-xs font-black flex items-center justify-center gap-2 shadow-2xl">
          <AlertTriangle size={14} /> SEM CONEXÃO COM O BANCO DE DADOS
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-md animate-in fade-in">
          <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl relative p-8">
            <button onClick={() => setShowAuthModal(false)} className="absolute top-6 right-6 text-gray-300 hover:text-pink-500 transition-colors"><X size={24} /></button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 gradient-bg rounded-2xl flex items-center justify-center text-white shadow-lg mx-auto mb-4"><Heart size={32} fill="currentColor" /></div>
              <h2 className="text-2xl font-black text-gray-800">{authMode === 'login' ? 'Entre no Pix Love' : 'Crie seu Perfil'}</h2>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-2">Para espalhar mimos e carinhos</p>
            </div>
            <form onSubmit={handleManualAuth} className="space-y-4">
              <input type="text" required value={formUsername} onChange={(e) => setFormUsername(e.target.value)} className="w-full bg-gray-50 border-2 border-transparent focus:border-pink-200 rounded-2xl px-5 py-4 outline-none font-bold text-gray-700 transition-all" placeholder="Nome de Usuário" />
              {authMode === 'register' && (
                <>
                  <input type="text" required value={formDisplayName} onChange={(e) => setFormDisplayName(e.target.value)} className="w-full bg-gray-50 border-2 border-transparent focus:border-pink-200 rounded-2xl px-5 py-4 outline-none font-bold text-gray-700 transition-all" placeholder="Seu Nome Completo" />
                  <input type="text" required value={formPixKey} onChange={(e) => setFormPixKey(e.target.value)} className="w-full bg-gray-50 border-2 border-transparent focus:border-pink-200 rounded-2xl px-5 py-4 outline-none font-bold text-gray-700 transition-all" placeholder="Sua Chave Pix" />
                </>
              )}
              <input type="password" required value={formPassword} onChange={(e) => setFormPassword(e.target.value)} className="w-full bg-gray-50 border-2 border-transparent focus:border-pink-200 rounded-2xl px-5 py-4 outline-none font-bold text-gray-700 transition-all" placeholder="Senha" />
              {authError && <p className="text-red-500 text-xs font-bold text-center bg-red-50 py-2 rounded-xl border border-red-100">{authError}</p>}
              <button type="submit" disabled={isAuthLoading} className="w-full gradient-bg text-white py-4 rounded-2xl font-black shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-all">
                {isAuthLoading ? <Loader2 className="animate-spin" /> : (authMode === 'login' ? 'ENTRAR AGORA' : 'CRIAR MINHA CONTA')}
              </button>
              <button type="button" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')} className="w-full text-center text-[10px] font-black text-pink-500 uppercase mt-4 hover:underline tracking-widest">
                {authMode === 'login' ? 'Não tem conta? Cadastre-se' : 'Já é de casa? Faça login'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 glass z-50 px-4 py-3 border-b border-pink-100 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={goHome}>
          <div className="w-10 h-10 gradient-bg rounded-xl flex items-center justify-center text-white shadow-lg"><Heart size={24} fill="currentColor" /></div>
          <h1 className="text-2xl font-black gradient-text tracking-tighter">Pix Love</h1>
        </div>
        <div>
          {isLoggedIn ? (
            <div className="flex items-center gap-3">
              <div onClick={() => {setActiveTab('profile'); navigate('/profile');}} className="w-9 h-9 rounded-xl overflow-hidden ring-2 ring-pink-100 cursor-pointer hover:scale-110 transition-transform shadow-md"><img src={user?.avatar_url} className="w-full h-full object-cover" /></div>
              <button onClick={() => { setIsLoggedIn(false); setUser(null); navigate('/'); }} className="text-gray-400 hover:text-red-500 transition-colors"><LogOut size={22} /></button>
            </div>
          ) : (
            <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); }} className="gradient-bg text-white px-5 py-2.5 rounded-xl font-black text-xs shadow-md active:scale-95 transition-all">ENTRAR</button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-xl mx-auto px-4 mt-20 md:mt-6 pb-24">
        {publicProfile ? (
          <div className="animate-in slide-in-from-bottom-12 duration-700">
            <div className="bg-white rounded-[3rem] p-8 shadow-2xl border border-pink-50 text-center relative overflow-hidden mb-6">
              <button onClick={goHome} className="absolute top-8 left-8 p-2 text-gray-300 hover:text-pink-500 transition-all"><ArrowLeft size={24} /></button>
              <div className="relative w-32 h-32 mx-auto mb-6">
                <img src={publicProfile.avatar_url} className="w-full h-full rounded-[2.5rem] object-cover ring-8 ring-pink-50 shadow-inner" />
                <div className="absolute -bottom-2 -right-2 bg-pink-500 text-white p-2 rounded-xl"><Heart size={18} fill="currentColor" /></div>
              </div>
              <h2 className="text-3xl font-black text-gray-800">{publicProfile.display_name}</h2>
              <p className="text-pink-500 font-black text-xs uppercase tracking-widest mb-8">@{publicProfile.username}</p>
              
              <div className="bg-pink-50/50 p-8 rounded-[3rem] border border-pink-100 text-left">
                <h3 className="text-xs font-black text-pink-600 uppercase mb-6 flex items-center gap-2 tracking-widest"><Send size={16} /> Enviar Pix agora</h3>
                
                {!isLoggedIn ? (
                  <div className="bg-white p-8 rounded-[2rem] border-2 border-dashed border-pink-200 text-center">
                    <Zap className="mx-auto text-amber-400 mb-4" size={32} />
                    <p className="text-gray-600 font-bold text-sm mb-6">Você precisa estar logado para enviar um mimo ao mural!</p>
                    <button onClick={() => {setAuthMode('login'); setShowAuthModal(true);}} className="gradient-bg text-white px-8 py-4 rounded-2xl font-black text-xs shadow-xl active:scale-95 transition-all">ENTRAR NO PIX LOVE</button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="relative">
                      <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-pink-400 text-lg">R$</span>
                      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className="w-full bg-white border-2 border-transparent focus:border-pink-200 rounded-[1.2rem] pl-14 pr-6 py-5 outline-none font-black text-2xl text-gray-800 shadow-sm transition-all" />
                    </div>
                    <div className="relative">
                      <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Escreva algo especial..." className="w-full bg-white border-2 border-transparent focus:border-pink-200 rounded-[1.2rem] px-6 py-6 outline-none font-medium text-gray-700 h-32 resize-none shadow-sm transition-all" />
                      <button onClick={handleGenerateMagicMessage} disabled={isGeneratingMessage} className="absolute bottom-4 right-4 text-[10px] font-black text-pink-500 uppercase flex items-center gap-1.5 bg-pink-50 px-3 py-2 rounded-full border border-pink-100 hover:bg-pink-100 transition-colors">
                        {isGeneratingMessage ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} IA
                      </button>
                    </div>
                    <button onClick={handleSendPix} disabled={isSending} className="w-full gradient-bg text-white py-6 rounded-[2rem] font-black text-lg shadow-xl shadow-pink-100 flex items-center justify-center gap-3 active:scale-95 transition-all">
                      {isSending ? <Loader2 className="animate-spin" /> : <Heart size={24} fill="currentColor" />}
                      ENVIAR AO MURAL
                    </button>
                    <div className="bg-white/60 p-4 rounded-2xl border border-pink-100/50 text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Chave Pix de {publicProfile.display_name.split(' ')[0]}</p>
                      <p className="font-black text-pink-600 break-all text-xs tracking-tighter">{publicProfile.pix_key}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {activeTab === 'feed' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
                {isLoggedIn && user && (
                  <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-0.5 rounded-[2.5rem] shadow-xl shadow-amber-100">
                    <div className="bg-white rounded-[2.4rem] p-8 text-center relative overflow-hidden">
                      <Zap className="absolute -top-2 -right-2 text-amber-500 opacity-10" size={80} />
                      <h3 className="text-xl font-black text-gray-800 mb-2">Seu Link do Pix Love 💎</h3>
                      <p className="text-gray-500 text-sm mb-6">Divulgue seu link para receber carinho e mimos!</p>
                      <div className="flex gap-3">
                        <button onClick={handleCopyLink} className="flex-1 bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 font-black text-[10px] text-gray-700 flex items-center justify-center gap-2 hover:bg-gray-200 transition-all shadow-sm active:scale-95">
                          {copySuccess ? <Check size={16} className="text-green-500" /> : <Copy size={16} />} {copySuccess ? 'COPIADO!' : 'COPIAR LINK'}
                        </button>
                        <button onClick={handleShareSocial} className="flex-1 gradient-bg text-white rounded-2xl py-4 font-black text-[10px] flex items-center justify-center gap-2 shadow-xl active:scale-95 transition-all uppercase tracking-widest">
                          <Share2 size={16} /> Compartilhar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button onClick={handleOpenSendTab} className="w-full gradient-bg p-8 rounded-[2.5rem] shadow-2xl shadow-pink-200 text-white flex items-center justify-between group transition-all active:scale-[0.98]">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shadow-inner"><Heart size={32} fill="white" className="animate-pulse" /></div>
                    <div className="text-left"><h3 className="text-2xl font-black leading-tight">Mandar um Mimo</h3><p className="text-pink-100 text-sm font-bold opacity-90">Faça alguém sorrir hoje</p></div>
                  </div>
                  <div className="bg-white/20 p-3 rounded-full group-hover:translate-x-2 transition-transform"><ArrowRight size={32} strokeWidth={3} /></div>
                </button>

                <div className="flex items-center justify-between px-2">
                  <h2 className="text-2xl font-black text-gray-800 tracking-tight">Mural do Amor ✨</h2>
                  <button onClick={fetchFeed} className="text-[11px] font-black text-pink-500 uppercase tracking-widest flex items-center gap-2 hover:opacity-70 transition-opacity"><History size={14} /> Atualizar</button>
                </div>
                
                <div className="space-y-6">
                  {transactions.length > 0 ? transactions.map((tx) => (
                    <div key={tx.id} className="bg-white rounded-[2.5rem] p-7 shadow-xl shadow-pink-100/20 border border-pink-50 relative overflow-hidden group hover:scale-[1.01] transition-transform">
                      <div className="absolute top-0 right-0 p-6 opacity-[0.03] -rotate-12 pointer-events-none"><Heart size={100} fill="#f73378" /></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-5">
                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-4">
                              <img src={tx.sender_avatar_url} className="w-10 h-10 rounded-xl object-cover ring-2 ring-white shadow-sm" />
                              <img src={tx.receiver_avatar_url} className="w-10 h-10 rounded-xl object-cover ring-2 ring-white shadow-sm" />
                            </div>
                            <div>
                              <p className="text-sm font-black text-gray-800 mb-0.5">
                                {tx.sender_name} <Heart size={10} className="inline text-pink-500 mx-1" fill="currentColor" /> {tx.receiver_name}
                              </p>
                              <p className="text-[10px] font-bold text-gray-400 uppercase">Mimo Enviado</p>
                            </div>
                          </div>
                          <span className="bg-pink-50 text-pink-600 font-black px-4 py-2 rounded-2xl text-base border border-pink-100">R$ {tx.amount.toFixed(2)}</span>
                        </div>
                        <div className="bg-pink-50/30 p-6 rounded-[1.5rem] italic text-[15px] text-gray-700 font-semibold leading-relaxed">
                          "{tx.message}"
                        </div>
                        <p className="text-[9px] text-gray-300 font-black mt-4 text-right uppercase tracking-widest">{new Date(tx.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-24 bg-white rounded-[3rem] border-2 border-dashed border-pink-100">
                      <Sparkles size={64} className="mx-auto text-pink-100 mb-4" />
                      <p className="text-gray-400 font-black text-sm uppercase tracking-widest px-8">O mural está vazio. Comece a espalhar carinho!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'send' && (
              <div className="bg-white rounded-[3rem] p-10 shadow-2xl border border-pink-50 animate-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-black text-gray-800 mb-10 flex items-center gap-3">Novo Carinho <Sparkles className="text-pink-500" size={32} /></h2>
                <div className="space-y-8">
                  <div className="space-y-2 relative">
                    <label className="text-[11px] font-black text-gray-400 uppercase ml-2 tracking-widest flex items-center gap-1">
                      <Search size={10} /> Para quem?
                    </label>
                    <div className="relative">
                      <input 
                        type="text" 
                        value={receiverName} 
                        onChange={handleReceiverNameChange} 
                        placeholder="Pesquisar usuários ou digitar nome..." 
                        className="w-full bg-pink-50/30 border-2 border-transparent focus:border-pink-200 rounded-2xl px-6 py-5 outline-none font-bold text-gray-700 shadow-inner transition-all" 
                      />
                      {isSearchingUsers && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <Loader2 size={16} className="animate-spin text-pink-400" />
                        </div>
                      )}
                    </div>
                    
                    {userSuggestions.length > 0 && (
                      <div className="absolute top-[calc(100%+8px)] left-0 right-0 z-20 bg-white rounded-2xl shadow-2xl border border-pink-100 overflow-hidden animate-in fade-in slide-in-from-top-2">
                        {userSuggestions.map(profile => (
                          <button 
                            key={profile.id}
                            onClick={() => selectUser(profile)}
                            className="w-full flex items-center gap-3 p-4 hover:bg-pink-50 text-left transition-colors border-b border-pink-50 last:border-0"
                          >
                            <img src={profile.avatar_url} className="w-10 h-10 rounded-xl object-cover shadow-sm" />
                            <div>
                              <p className="font-black text-gray-800 text-sm">{profile.display_name}</p>
                              <p className="text-[10px] text-pink-500 font-bold uppercase">@{profile.username}</p>
                            </div>
                            <ArrowRight size={14} className="ml-auto text-pink-200" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase ml-2 tracking-widest">Chave Pix do Destinatário</label>
                    <input type="text" value={receiverPixKey} onChange={(e) => setReceiverPixKey(e.target.value)} placeholder="E-mail, CPF, Celular..." className="w-full bg-pink-50/30 border-2 border-transparent focus:border-pink-200 rounded-2xl px-6 py-5 outline-none font-bold text-gray-700 shadow-inner transition-all" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase ml-2 tracking-widest">Valor do Mimo</label>
                    <div className="relative">
                      <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-pink-400 text-xl">R$</span>
                      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full bg-pink-50/30 border-2 border-transparent focus:border-pink-200 rounded-2xl pl-14 pr-6 py-5 outline-none font-black text-3xl text-gray-800 shadow-inner transition-all" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center ml-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Sua Mensagem</label>
                      <button onClick={handleGenerateMagicMessage} disabled={isGeneratingMessage} className="text-[10px] font-black text-pink-500 uppercase flex items-center gap-2 hover:opacity-70 transition-opacity">
                        {isGeneratingMessage ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Gerar com IA
                      </button>
                    </div>
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Diga algo inesquecível..." className="w-full bg-pink-50/30 border-2 border-transparent focus:border-pink-200 rounded-2xl px-6 py-6 outline-none font-medium text-gray-700 h-40 resize-none shadow-inner transition-all" />
                  </div>

                  <button onClick={handleSendPix} disabled={isSending} className="w-full gradient-bg text-white font-black py-6 rounded-[2.5rem] shadow-2xl text-xl flex items-center justify-center gap-4 active:scale-95 transition-all">
                    {isSending ? <Loader2 className="animate-spin" /> : <Send size={24} />} CONFIRMAR PIX LOVE
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'profile' && isLoggedIn && user && (
              <div className="bg-white rounded-[3.5rem] p-12 shadow-2xl border border-pink-50 text-center relative overflow-hidden animate-in slide-in-from-bottom-4 duration-500">
                <div className="absolute top-0 right-0 p-12 opacity-[0.03] -rotate-12 pointer-events-none"><Heart size={200} fill="#f73378" /></div>
                <div className="relative w-40 h-40 mx-auto mb-8">
                  <img src={user.avatar_url} className="w-full h-full rounded-[3rem] object-cover ring-[12px] ring-pink-50 shadow-inner" />
                  <button 
                    onClick={() => setIsEditingAvatar(!isEditingAvatar)} 
                    className="absolute -bottom-2 -right-2 bg-pink-500 text-white p-3 rounded-2xl shadow-xl hover:scale-110 active:scale-95 transition-all z-10"
                  >
                    <Camera size={24} fill="currentColor" />
                  </button>
                </div>

                {isEditingAvatar && (
                  <div className="mb-10 p-6 bg-pink-50/50 rounded-3xl border-2 border-pink-100 animate-in zoom-in-95 duration-200">
                    <div className="flex justify-center gap-4 mb-6">
                      <button 
                        onClick={() => setAvatarEditMode('upload')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${avatarEditMode === 'upload' ? 'bg-pink-500 text-white shadow-md' : 'bg-white text-pink-400'}`}
                      >
                        <Upload size={14} /> Upload
                      </button>
                      <button 
                        onClick={() => setAvatarEditMode('url')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${avatarEditMode === 'url' ? 'bg-pink-500 text-white shadow-md' : 'bg-white text-pink-400'}`}
                      >
                        <LinkIcon size={14} /> URL
                      </button>
                    </div>

                    {avatarEditMode === 'upload' ? (
                      <div className="space-y-4">
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-32 border-2 border-dashed border-pink-200 bg-white rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-pink-50/50 transition-colors"
                        >
                          {newAvatarUrl && avatarEditMode === 'upload' ? (
                            <img src={newAvatarUrl} className="h-full w-full object-contain p-2 rounded-2xl" />
                          ) : (
                            <>
                              <Upload className="text-pink-300 mb-2" size={32} />
                              <span className="text-[10px] font-black text-pink-400 uppercase tracking-widest">Escolher Foto (Máx 2MB)</span>
                            </>
                          )}
                        </div>
                        <input 
                          type="file" 
                          ref={fileInputRef} 
                          onChange={handleFileChange} 
                          accept="image/*" 
                          className="hidden" 
                        />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <input 
                          type="text" 
                          value={newAvatarUrl} 
                          onChange={(e) => setNewAvatarUrl(e.target.value)} 
                          placeholder="Link da sua imagem..." 
                          className="w-full bg-white border-2 border-transparent focus:border-pink-200 rounded-xl px-4 py-3 outline-none font-bold text-gray-700 shadow-sm"
                        />
                      </div>
                    )}

                    <div className="mt-4 flex gap-2">
                       <button 
                        onClick={() => { setIsEditingAvatar(false); setNewAvatarUrl(''); }} 
                        className="flex-1 bg-white text-gray-400 py-3 rounded-xl font-black text-xs border border-gray-100 active:scale-95 transition-all"
                      >
                        CANCELAR
                      </button>
                      <button 
                        onClick={handleSaveAvatar} 
                        disabled={isSavingAvatar || !newAvatarUrl}
                        className="flex-1 gradient-bg text-white py-3 rounded-xl font-black text-xs shadow-md disabled:opacity-50 active:scale-95 transition-all"
                      >
                        {isSavingAvatar ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'SALVAR FOTO'}
                      </button>
                    </div>
                  </div>
                )}

                <h2 className="text-4xl font-black text-gray-800 leading-tight mb-2">{user.display_name}</h2>
                <p className="text-pink-500 font-black text-xs uppercase tracking-[0.3em] mb-12 block">@{user.username}</p>
                
                <div className="grid grid-cols-2 gap-6 mb-12">
                  <div className="bg-pink-50/50 p-8 rounded-[2.5rem] border border-pink-100 shadow-inner transition-transform hover:scale-105">
                    <p className="text-4xl font-black text-pink-600 mb-1">0</p>
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Recebidos</p>
                  </div>
                  <div className="bg-pink-50/50 p-8 rounded-[2.5rem] border border-pink-100 shadow-inner transition-transform hover:scale-105">
                    <p className="text-4xl font-black text-pink-600 mb-1">R$ 0</p>
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Volume</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <button onClick={handleShareSocial} className="w-full gradient-bg text-white font-black py-6 rounded-[2.5rem] shadow-2xl text-xl flex items-center justify-center gap-4 active:scale-[0.98] transition-all">
                    <Share2 size={28} /> COMPARTILHAR MEU LINK
                  </button>
                  <button onClick={handleCopyLink} className="w-full bg-white text-pink-500 border-2 border-pink-100 font-black py-4 rounded-[2.5rem] shadow-md text-sm flex items-center justify-center gap-4 active:scale-[0.98] transition-all">
                    {copySuccess ? <Check size={20} /> : <Copy size={20} />} {copySuccess ? 'LINK COPIADO!' : 'COPIAR LINK DE PERFIL'}
                  </button>
                  <div className="bg-gray-50/80 p-5 rounded-3xl border border-gray-100">
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-2">Sua Chave Pix Registrada</p>
                    <p className="font-black text-gray-700 text-sm break-all">{user.pix_key}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Navigation Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 glass border-t border-pink-100 md:hidden flex justify-around items-center py-5 px-6 z-50 rounded-t-[3rem] shadow-2xl">
        <button onClick={goHome} className={`flex flex-col items-center gap-2 p-2 transition-all ${activeTab === 'feed' && !publicProfile ? 'text-pink-600 scale-125' : 'text-gray-400 hover:text-pink-400'}`}>
          <History size={28} strokeWidth={activeTab === 'feed' && !publicProfile ? 3 : 2} />
          <span className="text-[10px] font-black uppercase tracking-widest">Mural</span>
        </button>
        <button onClick={handleOpenSendTab} className={`-mt-14 w-20 h-20 rounded-[2.5rem] flex items-center justify-center text-white shadow-2xl shadow-pink-200 active:scale-90 transition-all ${activeTab === 'send' ? 'gradient-bg rotate-12' : 'bg-gray-800'}`}>
          <Send size={32} strokeWidth={3} />
        </button>
        <button onClick={() => isLoggedIn ? (setActiveTab('profile'), navigate('/profile')) : setShowAuthModal(true)} className={`flex flex-col items-center gap-2 p-2 transition-all ${activeTab === 'profile' ? 'text-pink-600 scale-125' : 'text-gray-400 hover:text-pink-400'}`}>
          <User size={28} strokeWidth={activeTab === 'profile' ? 3 : 2} />
          <span className="text-[10px] font-black uppercase tracking-widest">Perfil</span>
        </button>
      </nav>
    </div>
  );
};

export default App;
